import { Modal } from 'bootstrap'

import { ComponentRef, EventEmitter, Injectable, Type, ViewContainerRef } from '@angular/core'
import { ModalComponent } from '@modal/modal/modal.component'

export interface ModalBody {
  events: EventEmitter<'close' | 'submit'>
  modal: Modal
}

export type ModalOptions = {
  title: string,
  bodyInputs?: Object,
  closeOnSubmit?: boolean
  closeText?: string
  destroyOnClose?: boolean
  showDetails?: boolean
  size?: string,
  submitBtnColor?: 'green' | 'red'
  submitText?: string,
}

@Injectable({
  providedIn: 'root'
})
export class ModalService {
  private _appViewContainer: ViewContainerRef // Important Note: Must be set by AppComponent
  private _modalInstances: Map<string, ModalComponent> = new Map<string, ModalComponent>()
  private _singletonModalRefs: Map<string, ComponentRef<any>> = new Map<string, ComponentRef<any>>()
  private _uploadModalRef: ComponentRef<any> = null

  constructor() { }

  initialize(container: ViewContainerRef) {
    this._appViewContainer = container
  }

  clearUploadModal() {
    this._uploadModalRef = null
  }

  /** Inserts the Component as the body of a Modal with a configurable header and footer */
  insertIntoModal(componentBody: any, options: ModalOptions) {
    const name = componentBody.name

    if (!this._modalInstances.has(name)) {
      const modalComponentRef = this._appViewContainer.createComponent<ModalComponent>(ModalComponent)
      var modalComponent = modalComponentRef.instance

      // Configure the modal with the options passed in
      modalComponent.componentBody = componentBody
      modalComponent.modal = new Modal(modalComponent.elementRef.nativeElement.children[0], { focus: false })
      modalComponent.options = options
      options.closeOnSubmit = options.closeOnSubmit ?? true

      this._modalInstances.set(name, modalComponent)

      modalComponent.modal.show()

      const subscription = modalComponent.events.subscribe(event => {
        if (event != 'submit' || options.closeOnSubmit) {
          modalComponent.modal.hide()

          if (event == 'submit' || (event == 'close' && options.destroyOnClose)) {
            this._modalInstances.delete(name)

            subscription.unsubscribe()

            setTimeout(() => modalComponentRef.destroy())
          }
        }
      })
    } else {
      var modalComponent = this._modalInstances.get(name)

      modalComponent.modal.show()
    }

    return modalComponent
  }

  /** Shows the components view in a Modal */
  showAsModal<T>(component: Type<T>, modalOptions?: { focus?: boolean, backdrop?: boolean, destroyOnClose?: boolean, bodyInputs?: Partial<T>, singletonKey?: string }) {
    const singletonKey = modalOptions?.singletonKey
    const existingSingletonRef = singletonKey ? this._singletonModalRefs.get(singletonKey) as ComponentRef<T> : null

    if (existingSingletonRef) {
      if (modalOptions?.bodyInputs) {
        Object.assign(existingSingletonRef.instance, modalOptions.bodyInputs)
      }

      const modal = (existingSingletonRef.instance as any).modal
      modal.show()
      return Promise.resolve(existingSingletonRef)
    }

    // If this is the upload modal and we have an existing instance, show it instead of creating a new one
    if (component.name === 'UploadProjectFilesComponent' && this._uploadModalRef) {
      const modal = (this._uploadModalRef.instance as any).modal
      modal.show()
      return Promise.resolve(this._uploadModalRef)
    }

    const componentRef = this._appViewContainer.createComponent(component)
    const [modalElement] = componentRef.location.nativeElement.children
    const modal = new Modal(modalElement, { focus: modalOptions?.focus ?? true, backdrop: modalOptions?.backdrop ?? true })

    //@ts-ignore
    componentRef.instance.modal = modal

    // Store the upload modal reference
    if (component.name === 'UploadProjectFilesComponent') {
      this._uploadModalRef = componentRef
    }

    if (singletonKey) {
      this._singletonModalRefs.set(singletonKey, componentRef)
    }

    const destroy = modalOptions?.destroyOnClose ?? true
    if (destroy) {
      const handleHidden = () => {
        // Only destroy if this isn't the upload modal or if uploads are complete
        const instance = componentRef.instance as any
        if (component.name !== 'UploadProjectFilesComponent' || !instance.submitting) {
          if (component.name === 'UploadProjectFilesComponent') {
            this._uploadModalRef = null
          }
          if (singletonKey) {
            this._singletonModalRefs.delete(singletonKey)
          }
          componentRef.destroy()
          modalElement.removeEventListener('hidden.bs.modal', handleHidden)
        }
      }
      modalElement.addEventListener('hidden.bs.modal', handleHidden)
    }

    if (modalOptions?.bodyInputs) {
      Object.assign(componentRef.instance, modalOptions.bodyInputs)
    }

    modal.show()

    return new Promise<ComponentRef<T>>(
      (resolve) => setTimeout(() => resolve(componentRef))
    )
  }
}